import { useState } from "react";
import toast from "react-hot-toast";
import cancel from "../assets/images/navbar/cancel.png";
import bank from "../assets/images/payment/bank.png";
import cod from "../assets/images/payment/cod.png";
import { orderService, RazorpayOrderData } from "../services";
import OrderSuccess from "./OrderSuccess";
import { useProfile } from "../context/ProfileContext";
import { useCart } from "../context/CartContext";

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface CartItem {
  dishId: string;
  restaurantId: string;
  quantity: number;
  variantId?: string | null;
  addons?: any[];
}

interface PaymentModeProps {
  onClose: () => void;
  total: number;
  restaurantId: string;
  cartItems: CartItem[];
  couponCode?: string;
  deliveryInstructions?: string;
  cookingInstructions?: string;
  deliveryAddress?: {
    fullName: string;
    phone: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    pincode: string;
    geoLocation?: {
      type: string;
      coordinates: [number, number];
    };
  };
}

const PaymentMode = ({
  onClose,
  total,
  restaurantId,
  cartItems,
  couponCode,
  deliveryInstructions = "",
  cookingInstructions = "",
  deliveryAddress,
}: PaymentModeProps) => {
  // Mobile maps online + COD as the real payment methods
  const [selectedPayment, setSelectedPayment] = useState<string>("cod");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const { getDeliveryAddress, profile } = useProfile();
  const { clearCart } = useCart();

  const openRazorpayCheckout = (
    createdOrderId: string,
    razorpay: RazorpayOrderData
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error("Razorpay SDK not loaded"));
        return;
      }

      const options = {
        description: "Order payment",
        currency: razorpay.currency || "INR",
        key: razorpay.key,
        amount: String(razorpay.amount),
        name: "Dudos",
        order_id: razorpay.razorpayOrderId,
        prefill: {
          email: profile?.email ?? "",
          contact: deliveryAddress?.phone ?? profile?.phoneNumber ?? "",
          name:
            deliveryAddress?.fullName ??
            [profile?.firstName, profile?.lastName].filter(Boolean).join(" "),
        },
        theme: { color: "#7402A7" },
        handler: async () => {
          try {
            await clearCart();
          } catch {
            // cart may already be cleared by backend
          }
          setOrderId(createdOrderId);
          setShowSuccess(true);
          resolve();
        },
        modal: {
          ondismiss: () => {
            reject(new Error("Payment was cancelled"));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response: any) => {
        reject(
          new Error(
            response?.error?.description ||
              "Something went wrong while processing payment"
          )
        );
      });
      rzp.open();
    });
  };

  const handlePayment = async () => {
    setIsLoading(true);

    try {
      const finalAddress = deliveryAddress || getDeliveryAddress();
      if (!finalAddress?.fullName || !finalAddress?.addressLine1) {
        toast.error("Please select a delivery address");
        return;
      }

      const orderData = {
        restaurantId,
        items: cartItems.map((item) => ({
          dishId: item.dishId,
          variantId: item.variantId || null,
          addons: item.addons || [],
          quantity: item.quantity,
        })),
        paymentMethod: selectedPayment,
        couponCode: couponCode || undefined,
        cookingInstructions: cookingInstructions.trim() || undefined,
        deliveryInstructions: deliveryInstructions.trim() || undefined,
        deliveryAddress: finalAddress,
      };

      const response: any = await orderService.createOrder(orderData);

      if (!response?.status || !response?.data) {
        toast.error(response?.message || "Failed to place order");
        return;
      }

      const createdOrderId = response.data.orderId;

      if (selectedPayment === "online" && response.data.razorpay) {
        try {
          await openRazorpayCheckout(createdOrderId, response.data.razorpay);
        } catch (err: any) {
          toast.error(err?.message || "Payment failed. Please try again.");
        }
        return;
      }

      try {
        await clearCart();
      } catch {
        // ignore
      }

      toast.success("Order placed successfully!");
      setOrderId(createdOrderId);
      setShowSuccess(true);
    } catch (error: any) {
      console.error("Order creation failed:", error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Failed to create order. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return <OrderSuccess onClose={onClose} orderId={orderId || undefined} />;
  }

  return (
    <div>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1003] transition-colors duration-300"
        onClick={onClose}
      />

      <div className="fixed inset-0 flex items-center justify-center z-[1004] pointer-events-none">
        <div
          className="w-[545px] max-w-[95vw] p-8 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300"
          style={{ boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="absolute top-6 right-6 h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
          >
            <img src={cancel} alt="Close" className="h-[18px] w-[18px] dark:invert" />
          </div>

          <div className="mb-[36px]">
            <p className="dark:text-white font-urbanist text-[20px] font-semibold">
              Select Payment Method
            </p>
          </div>

          <div className="border-2 dark:border-gray-700 p-[12px] rounded-[16px] dark:bg-gray-750 space-y-0">
            <div className="flex justify-between items-center pb-[12px] border-b-2 dark:border-gray-700">
              <div className="flex justify-center items-center gap-[10px]">
                <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                  <img src={bank} alt="online" className="h-[27px] w-[27px]" />
                </div>
                <div>
                  <h6 className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[160%]">
                    Pay Online
                  </h6>
                  <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-normal leading-[140%]">
                    UPI, cards & net banking
                  </p>
                </div>
              </div>
              <input
                type="radio"
                name="payment"
                value="online"
                checked={selectedPayment === "online"}
                onChange={(e) => setSelectedPayment(e.target.value)}
                className="cursor-pointer"
              />
            </div>

            <div className="flex justify-between items-center pt-[12px]">
              <div className="flex justify-center items-center gap-[10px]">
                <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                  <img src={cod} alt="cod" className="h-[27px] w-[27px]" />
                </div>
                <div>
                  <h6 className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[160%]">
                    Cash on Delivery
                  </h6>
                  <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-normal leading-[140%]">
                    Pay when your order arrives
                  </p>
                </div>
              </div>
              <input
                type="radio"
                name="payment"
                value="cod"
                checked={selectedPayment === "cod"}
                onChange={(e) => setSelectedPayment(e.target.value)}
                className="cursor-pointer"
              />
            </div>
          </div>

          <button
            onClick={handlePayment}
            disabled={isLoading}
            className="w-full flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            <span className="w-full text-white text-center font-urbanist text-[20px] font-semibold leading-[140%]">
              {isLoading ? "Processing..." : `Pay ₹${total.toFixed(2)}`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMode;
